import React, { useState } from 'react'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'

import LazyImage from '../display/LazyImage'
import ChevronDownIcon from '../icons/ChevronDown'
import LinkOrA from '../cards/shared/LinkOrA'
import { Arrow, Wrapper, SliderWrapper } from '../cards/shared/CardSlider'

import HorizontallyScrollable from './HorizontallyScrollable'
import StoryCredit from './StoryCredit'

const StoryImage = styled(LazyImage)`
  border-radius: 8px;
  max-height: 640px;

  ${mediaMax.md`
    max-height: unset;
    height: 50vh;
  `}
`

const Slide = styled.div<{ margin: number }>`
  width: 65vw;
  max-width: 1200px;
  margin-left: ${({ margin }) => margin}px;

  ${mediaMax.md`
    width: 80vw;
  `}

  & > *:last-child {
    margin-top: 20px;
  }
`

const Outer = styled.div`
  position: relative;
  min-height: 320px;

  ${media.md`
    min-height: 770px;
  `}

  .swiper-slide {
    width: auto;
  }
`

type Story = {
  id: string
  url: string
  title: string
  promoImage: {
    large: string
  }
  user: {
    id: string
    firstName: string
    avatar: {
      thumbLg: string
    }
  }
}

const StoriesCarousel: React.FC<{
  stories: Story[]
}> = ({ stories }) => {
  const [swiperInstance, setSwiper] = useState(null)
  const { md: isLargerThanMd } = useMediaQueryContext()
  const isSmallerThanMd = !isLargerThanMd

  const goNext = () => {
    if (swiperInstance !== null) {
      swiperInstance.slideNext()
    }
  }
  const goPrev = () => {
    if (swiperInstance !== null) {
      swiperInstance.slidePrev()
    }
  }

  if (isLargerThanMd) {
    return (
      <Outer>
        {Swiper && (
          <Wrapper>
            <SliderWrapper>
              <Swiper
                onSwiper={setSwiper}
                spaceBetween={40}
                slidesPerView="auto"
                loop
              >
                {stories.map(({ id, promoImage, title, url, user }) => (
                  <SwiperSlide key={id}>
                    <Slide margin={isSmallerThanMd ? 0 : 40}>
                      <LinkOrA asLink={isSmallerThanMd} to={url}>
                        <StoryImage data-src={promoImage.large} />
                        <noscript>
                          <StoryImage src={promoImage.large} />
                        </noscript>
                      </LinkOrA>
                      <StoryCredit
                        avatar={user.avatar}
                        firstName={user.firstName}
                        storyTitle={title}
                        storyUrl={url}
                      />
                    </Slide>
                  </SwiperSlide>
                ))}
              </Swiper>
            </SliderWrapper>
            <>
              <Arrow
                onClick={goPrev}
                show
                left
                style={{ left: 60, top: '39%' }}
              >
                <ChevronDownIcon />
              </Arrow>
              <Arrow
                onClick={goNext}
                show
                right
                style={{ right: 60, top: '39%' }}
              >
                <ChevronDownIcon />
              </Arrow>
            </>
          </Wrapper>
        )}
      </Outer>
    )
  }

  return (
    <HorizontallyScrollable>
      {stories.map(({ id, promoImage, title, url, user }) => (
        <Slide key={id} margin={isSmallerThanMd ? 0 : 40}>
          <LinkOrA asLink={isSmallerThanMd} to={url}>
            <StoryImage data-src={promoImage.large} />
            <noscript>
              <StoryImage src={promoImage.large} />
            </noscript>
          </LinkOrA>
          <StoryCredit
            avatar={user.avatar}
            firstName={user.firstName}
            storyTitle={title}
            storyUrl={url}
          />
        </Slide>
      ))}
    </HorizontallyScrollable>
  )
}

export default StoriesCarousel
